import React, { useState } from 'react';
import BrandLayout from '@/components/BrandLayout';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Upload, FileText, Check, AlertTriangle, Download } from 'lucide-react';

const SAMPLE_CSV = `name,sku,category,subcategory,description,mrp,price,image,stock
Linen Resort Shirt,LRS-001,fashion,menswear,Breathable linen short sleeve resort shirt,3499,2799,https://images.unsplash.com/photo-1602810318383-e386cc2a3ccf?w=900,42
Silk Slip Dress,SSD-002,fashion,womenswear,Bias-cut silk slip dress with adjustable straps,7990,5990,https://images.unsplash.com/photo-1469334031218-e382a71b716b?w=900,18
Premium Leather Sneakers,PLS-003,footwear,sneakers,Italian leather low-top with cushioned insole,9990,7990,https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=900,25`;

export default function BrandInventory() {
    const [csv, setCsv] = useState('');
    const [filename, setFilename] = useState('');
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    const onFile = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.name.toLowerCase().endsWith('.csv')) {
            toast.error('Please upload a .csv file');
            return;
        }
        setFilename(file.name);
        const reader = new FileReader();
        reader.onload = (ev) => setCsv(ev.target.result);
        reader.readAsText(file);
    };

    const upload = async () => {
        if (!csv.trim()) return toast.error('Paste or upload a CSV first');
        setBusy(true); setResult(null);
        try {
            const { data } = await api.post('/brand/products/bulk-upload', { csv });
            setResult(data);
            toast.success(`${data.created} created, ${data.updated} updated`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Upload failed');
        } finally { setBusy(false); }
    };

    const downloadSample = () => {
        const blob = new Blob([SAMPLE_CSV], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = 'inorbit-brand-catalog-template.csv';
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
    };

    return (
        <BrandLayout>
            <div className="p-10 max-w-5xl" data-testid="brand-inventory-page">
                <div className="mb-10">
                    <div className="overline">Bulk Operations</div>
                    <h1 className="font-serif text-4xl text-inorbit-blue mt-2">Inventory Upload</h1>
                    <div className="text-sm text-text-secondary mt-1">Upload thousands of SKUs in one go — CSV format.</div>
                </div>

                <div className="grid md:grid-cols-3 gap-8">
                    <div className="md:col-span-2 space-y-8">
                        <div className="bg-white border border-border-subtle rounded-md p-8">
                            <div className="flex items-center justify-between mb-4">
                                <div>
                                    <div className="overline">Step 1</div>
                                    <h2 className="font-serif text-2xl text-inorbit-blue mt-1">Upload CSV</h2>
                                </div>
                                <button onClick={downloadSample} className="btn-link text-[11px]" data-testid="download-sample-csv">
                                    <Download size={12} /> Download template
                                </button>
                            </div>
                            <label className="block border-2 border-dashed border-border-medium rounded-md p-12 text-center cursor-pointer hover:border-inorbit-orange hover:bg-cream transition-colors" data-testid="csv-dropzone">
                                <Upload size={28} className="mx-auto text-inorbit-orange mb-3" strokeWidth={1.4} />
                                <div className="font-serif text-lg text-inorbit-blue">Choose .csv file</div>
                                <div className="text-xs text-text-tertiary mt-1">{filename || 'Drag & drop or click to browse'}</div>
                                <input type="file" accept=".csv,text/csv" onChange={onFile} className="hidden" data-testid="csv-file-input" />
                            </label>
                            <div className="text-center mt-4 text-xs text-text-tertiary">— or paste CSV below —</div>
                            <textarea
                                value={csv}
                                onChange={(e) => { setCsv(e.target.value); setFilename(''); }}
                                placeholder="Paste CSV content here (header row required)"
                                rows={8}
                                className="input-box font-mono text-xs mt-4 resize-y"
                                data-testid="csv-paste-area"
                            />
                            <button onClick={upload} disabled={busy || !csv.trim()} className="btn-primary mt-6 w-full justify-center" data-testid="csv-upload-btn">
                                {busy ? 'Processing…' : 'Upload Catalog'}
                            </button>
                        </div>

                        {result && (
                            <div className="bg-white border border-border-subtle rounded-md p-8" data-testid="upload-result">
                                <div className="overline mb-4">Upload Result</div>
                                <div className="grid grid-cols-3 gap-4 mb-6">
                                    <Stat label="Created" value={result.created} icon={Check} color="text-emerald-700" />
                                    <Stat label="Updated" value={result.updated} icon={Check} color="text-inorbit-blue" />
                                    <Stat label="Errors" value={result.errors?.length || 0} icon={AlertTriangle} color={result.errors?.length ? 'text-rose-700' : 'text-text-tertiary'} />
                                </div>
                                {result.errors?.length > 0 && (
                                    <div className="bg-rose-50 border border-rose-200 rounded p-4 text-xs">
                                        <div className="font-semibold text-rose-700 mb-2">Failed rows:</div>
                                        {result.errors.slice(0, 10).map((er, i) => (
                                            <div key={i} className="font-mono">Row {er.row}: {er.error}</div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="space-y-4">
                        <div className="bg-inorbit-blue text-white rounded-md p-6">
                            <FileText size={20} className="text-inorbit-yellow mb-3" strokeWidth={1.4} />
                            <div className="overline text-inorbit-yellow mb-2">CSV Columns</div>
                            <ul className="text-xs space-y-2 text-white/90">
                                <li><span className="text-inorbit-yellow font-semibold">name</span> — required</li>
                                <li><span className="text-inorbit-yellow font-semibold">sku</span> — auto-generated if blank</li>
                                <li>category, subcategory</li>
                                <li>description</li>
                                <li><span className="text-inorbit-yellow font-semibold">mrp, price</span></li>
                                <li>image (URL)</li>
                                <li>stock (integer)</li>
                            </ul>
                            <div className="text-[10px] uppercase tracking-widest text-white/40 mt-6">
                                Powered by <span className="text-inorbit-yellow font-semibold">Fundle.ai</span>
                            </div>
                        </div>
                        <div className="bg-inorbit-yellow text-inorbit-blue rounded-md p-6">
                            <div className="overline text-inorbit-blue mb-2">Tip</div>
                            <div className="text-sm font-semibold leading-relaxed">Existing SKUs are updated in place — re-upload anytime to sync stock & pricing.</div>
                        </div>
                    </div>
                </div>
            </div>
        </BrandLayout>
    );
}

function Stat({ label, value, icon: Icon, color }) {
    return (
        <div className="text-center bg-cream rounded p-4">
            <Icon size={20} strokeWidth={1.4} className={`mx-auto ${color}`} />
            <div className="font-serif text-3xl text-inorbit-blue mt-2">{value}</div>
            <div className="text-[10px] uppercase tracking-widest text-text-tertiary mt-1">{label}</div>
        </div>
    );
}
